import React, { useState, useEffect } from 'react';
import { History, ChevronRight, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { adminAPI, i18nAPI, categoriesAPI, pricingAPI } from '../../services/api';
import { marginOf, suggestedPrice } from '../../services/margin';
import { searchTokens } from '../SearchHighlight';
import { useAdminProductSearch } from '../../hooks/useAdminProductSearch';
import AdminProductCards from '../AdminProductCards';
import PriceHistoryList from '../PriceHistoryList';
import ProductsBulkImprove from '../ProductsBulkImprove';
import ProductsSeoBulk from '../ProductsSeoBulk';
import UnmatchedFitmentsBanner from '../UnmatchedFitmentsBanner';
import CompatSyncCard from '../CompatSyncCard';
import CompatConflictsCard from '../CompatConflictsCard';
import EquivBrandStatsCard from '../EquivBrandStatsCard';
import { BulkCategoriesBar } from '../BulkCategoriesBar';
import { PricingToolsPanel } from '../PricingToolsPanel';
import CatalogImportModal from '../CatalogImportModal';
import WooImportModal from '../WooImportModal';
import ProductAiBatchModal from '../ProductAiBatchModal';
import ProductDescriptionDraftsModal from '../ProductDescriptionDraftsModal';
import AdminProductsTable from './AdminProductsTable';
import { AdminProductsFilters, AdminProductsActions } from './AdminProductsToolbar';

const LANGS4 = ['en', 'de', 'es', 'it'];
const EMPTY_VEHICLE = { brand: '', model: '', engine: '' };

const AdminProductsTab = ({ products, setProducts, searchTerm, vatRate, defaultMargin, seoGradeFilter, setSeoGradeFilter, seoSort, setSeoSort, onEdit, onNew, onDelete, onEditById, loadData, refreshProductsAndStats, setActiveTab }) => {
  const [f, setF] = useState({ lowMarginOnly: false, unpublishedOnly: false, categoryFilter: '', subcategoryFilter: '', vehicleFilter: EMPTY_VEHICLE, noCompatOnly: false, fuelFilter: '' });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const [allCategories, setAllCategories] = useState([]);
  useEffect(() => { categoriesAPI.getAll().then(setAllCategories).catch(() => setAllCategories([])); }, []);
  const [selectedIds, setSelectedIds] = useState([]);
  const [applyingPrice, setApplyingPrice] = useState('');
  const [exporting, setExporting] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [modal, setModal] = useState(null); // 'catalog' | 'woo' | 'ai' | 'descriptions'
  const [productsJob, setProductsJob] = useState(null);

  const query = { term: searchTerm, lowMargin: f.lowMarginOnly, unpublished: f.unpublishedOnly, seoGrade: seoGradeFilter, sort: seoSort, category: f.categoryFilter, subcategory: f.subcategoryFilter, vehicle: f.vehicleFilter, noCompat: f.noCompatOnly, fuel: f.fuelFilter };
  const productSearch = useAdminProductSearch({ ...query, refreshKey: products });
  const filtered = productSearch.items;
  const hlTokens = searchTokens(searchTerm);

  const missingDescriptions = products.filter(p => !(p.description || '').trim()).length;
  const untranslatedProducts = products.filter(p => LANGS4.some(l => !p.name_i18n?.[l] || ((p.description || '').trim() && !p.description_i18n?.[l]))).length;
  const unpublishedCount = products.filter(p => p.published === false).length;
  const margins = products.map(p => marginOf(p, vatRate)).filter(m => m.level !== 'none');
  const avgMargin = margins.length ? margins.reduce((n, m) => n + m.pct, 0) / margins.length : null;
  const lowMarginCount = margins.filter(m => m.level === 'low').length;
  const belowSuggested = products.filter(p => suggestedPrice(p, vatRate, defaultMargin)?.below);
  const allVisibleSelected = filtered.length > 0 && filtered.every(p => selectedIds.includes(p.id));
  const toggle = (id) => setSelectedIds(ids => (ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]));
  const toggleAll = () => setSelectedIds(ids => (allVisibleSelected ? ids.filter(id => !filtered.some(p => p.id === id)) : [...new Set([...ids, ...filtered.map(p => p.id)])]));

  useEffect(() => {
    if (!productsJob?.running) return undefined;
    const id = setInterval(async () => {
      try {
        const st = await i18nAPI.status();
        const j = st.jobs?.products;
        setProductsJob(j || null);
        if (j && !j.running) {
          clearInterval(id);
          await loadData();
          toast[j.errors?.length ? 'error' : 'success'](`${j.done} produit(s) traduits en 4 langues${j.errors?.length ? ` · ${j.errors.length} erreur(s)` : ''}`);
        }
      } catch { /* retry */ }
    }, 3000);
    return () => clearInterval(id);
  }, [productsJob?.running]); // eslint-disable-line react-hooks/exhaustive-deps

  const translateProducts = async () => {
    if (!window.confirm(`Traduire automatiquement (IA) le nom et la description des ${untranslatedProducts} produit(s) non traduits, ainsi que le glossaire des caractéristiques (libellés, types, valeurs), en anglais, allemand, espagnol et italien ? Les traductions existantes sont conservées (tâche en arrière-plan).`)) return;
    try {
      const r = await i18nAPI.translateProducts(null, false);
      if (!r.started) { toast.error(r.message); return; }
      setProductsJob(r.job);
    } catch (e) { toast.error(e.response?.data?.detail || 'Traduction impossible'); }
  };

  const exportSearch = async (format) => {
    setExporting(format);
    try { await adminAPI.exportSearch(format, { q: searchTerm, ...query }); toast.success(`Export ${format.toUpperCase()} téléchargé`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Export impossible'); }
    finally { setExporting(''); }
  };

  const applySuggested = async (product) => {
    setApplyingPrice(product.id);
    try {
      const r = await pricingAPI.apply(product.id);
      toast.success(`${product.name} : ${r.old_price.toFixed(2)} € → ${r.new_price.toFixed(2)} €`);
      await refreshProductsAndStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Impossible d\'appliquer le prix conseillé'); }
    finally { setApplyingPrice(''); }
  };

  const applyAllSuggested = async (count) => {
    if (!window.confirm(`Appliquer le prix conseillé aux ${count} produit(s) vendus en dessous ? Les prix barrés seront retirés.`)) return;
    setApplyingPrice('all');
    try {
      const r = await pricingAPI.applyAll();
      toast.success(r.message);
      await refreshProductsAndStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Mise à jour impossible'); }
    finally { setApplyingPrice(''); }
  };

  const close = () => setModal(null);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 [&>button]:w-full sm:[&>button]:w-auto">
        <div className="w-full xl:w-auto">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="products-title">Produits ({productSearch.total}{searchTerm.trim() || f.lowMarginOnly ? ` / ${products.length}` : ''})</h1>
          <AdminProductsFilters f={f} set={set} seoGradeFilter={seoGradeFilter} setSeoGradeFilter={setSeoGradeFilter} allCategories={allCategories} margins={margins} avgMargin={avgMargin} lowMarginCount={lowMarginCount} belowSuggested={belowSuggested} applyingPrice={applyingPrice} onApplyAll={applyAllSuggested} unpublishedCount={unpublishedCount} />
        </div>
        <AdminProductsActions
          hasFilter={!!(f.vehicleFilter.brand || f.noCompatOnly || f.fuelFilter || searchTerm.trim() || f.categoryFilter)} total={productSearch.total} exporting={exporting} onExport={exportSearch}
          onCatalogImport={() => setModal('catalog')} onWooImport={() => setModal('woo')} missingDescriptions={missingDescriptions} onDescriptions={() => setModal('descriptions')}
          productsJob={productsJob} untranslatedProducts={untranslatedProducts} onTranslate={translateProducts}
          onToggleHistory={() => { setShowHistory(v => !v); setHistoryKey(k => k + 1); }} onAiBatch={() => setModal('ai')} onNew={onNew}
        />
      </div>

      {showHistory && (
        <div className="bg-white rounded-lg shadow-sm p-5 mb-6" data-testid="products-history-panel">
          <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><History size={18} className="text-orange-500" /> 50 derniers changements de prix & stocks</h2>
          <PriceHistoryList limit={50} refreshKey={historyKey} />
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-4 mb-6" data-testid="products-bulk-improve-panel">
        <ProductsBulkImprove onDone={loadData} />
        <div className="mt-3"><ProductsSeoBulk onDone={loadData} /></div>
      </div>

      <UnmatchedFitmentsBanner refreshKey={products} onOpen={() => setActiveTab('vehicles')} />
      <CompatSyncCard onChanged={loadData} onEdit={onEditById} />
      <CompatConflictsCard refreshKey={products} onChanged={loadData} onEdit={onEditById} />
      <BulkCategoriesBar categories={allCategories} selectedIds={selectedIds} selectedProducts={filtered.filter(p => selectedIds.includes(p.id))} onClear={() => setSelectedIds([])} onDone={() => { setSelectedIds([]); loadData(); }} onImport={() => setModal('catalog')} />
      <PricingToolsPanel refreshKey={products} onChanged={loadData} />
      <EquivBrandStatsCard />

      <div className="md:hidden space-y-3">
        <AdminProductCards products={filtered} vatRate={vatRate} selectedIds={selectedIds} onToggleSelect={toggle} onEdit={onEdit} onDelete={onDelete} onSaved={refreshProductsAndStats} />
        {!productSearch.loading && filtered.length === 0 && (
          <p className="bg-white rounded-lg shadow-sm px-4 py-8 text-center text-sm text-gray-500" data-testid="admin-products-empty-mobile">{searchTerm.trim() ? <>Aucun produit ne correspond à « {searchTerm.trim()} »</> : 'Aucun produit ne correspond aux filtres'}</p>
        )}
        <div className="bg-white rounded-lg shadow-sm flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm text-gray-600" data-testid="admin-products-pagination-mobile">
          <span>{productSearch.loading || productSearch.searching ? 'Recherche…' : `${productSearch.total} produit${productSearch.total > 1 ? 's' : ''} · page ${productSearch.page} / ${productSearch.pages}`}</span>
          <div className="flex items-center gap-1">
            <Button type="button" variant="outline" size="sm" data-testid="admin-products-prev-page-mobile" disabled={productSearch.page <= 1} onClick={() => productSearch.setPage(p => p - 1)}><ChevronLeft size={16} /></Button>
            <Button type="button" variant="outline" size="sm" data-testid="admin-products-next-page-mobile" disabled={productSearch.page >= productSearch.pages} onClick={() => productSearch.setPage(p => p + 1)}><ChevronRight size={16} /></Button>
          </div>
        </div>
      </div>

      <AdminProductsTable
        products={filtered} productSearch={productSearch} searchTerm={searchTerm} hlTokens={hlTokens} vatRate={vatRate} defaultMargin={defaultMargin}
        selectedIds={selectedIds} onToggle={toggle} onToggleAll={toggleAll} allVisibleSelected={allVisibleSelected}
        onEdit={onEdit} onDelete={onDelete} onSaved={refreshProductsAndStats} applyingPrice={applyingPrice} onApplySuggested={applySuggested} seoSort={seoSort} setSeoSort={setSeoSort}
      />

      {modal === 'catalog' && <CatalogImportModal onClose={close} onApplied={refreshProductsAndStats} />}
      {modal === 'woo' && <WooImportModal onClose={close} onApplied={refreshProductsAndStats} />}
      {modal === 'descriptions' && <ProductDescriptionDraftsModal missingCount={missingDescriptions} onClose={close} onApplied={(id, description) => setProducts(ps => ps.map(p => (p.id === id ? { ...p, description } : p)))} />}
      {modal === 'ai' && <ProductAiBatchModal onClose={close} onCreated={refreshProductsAndStats} />}
    </>
  );
};

export default AdminProductsTab;
